import { ApiError } from '@/api/client';
import type { TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import { describeActionError } from '@/pages/Exceptions/shared';
import type { InboundLine } from '@/api/types';

export type StepAction = 'START' | 'ARRIVE' | 'FINISH' | 'CLOSE';

export const DISCREPANCIES = ['NONE', 'SHORTAGE', 'OVERAGE', 'WRONG_ITEM', 'DAMAGED', 'NO_BARCODE', 'EXPIRED'] as const;
export type DiscrepancyCode = (typeof DISCREPANCIES)[number];
export const PARTIES = ['UNDECIDED', 'WAREHOUSE', 'OWNER', 'CARRIER', 'CHANNEL'] as const;
export type PartyCode = (typeof PARTIES)[number];

const DISC_KEYS: Record<DiscrepancyCode, TranslationKey> = {
  NONE: 'rcv.disc.NONE', SHORTAGE: 'rcv.disc.SHORTAGE', OVERAGE: 'rcv.disc.OVERAGE', WRONG_ITEM: 'rcv.disc.WRONG_ITEM',
  DAMAGED: 'rcv.disc.DAMAGED', NO_BARCODE: 'rcv.disc.NO_BARCODE', EXPIRED: 'rcv.disc.EXPIRED',
};
const PARTY_KEYS: Record<PartyCode, TranslationKey> = {
  UNDECIDED: 'rcv.party.UNDECIDED', WAREHOUSE: 'rcv.party.WAREHOUSE', OWNER: 'rcv.party.OWNER', CARRIER: 'rcv.party.CARRIER', CHANNEL: 'rcv.party.CHANNEL',
};
export const discrepancyLabel = (t: TranslateFn, code: string): string => (code in DISC_KEYS ? t(DISC_KEYS[code as DiscrepancyCode]) : t('rcv.disc.NONE'));
export const partyLabel = (t: TranslateFn, code: string): string => (code in PARTY_KEYS ? t(PARTY_KEYS[code as PartyCode]) : t('rcv.party.UNDECIDED'));

export type NextStepInput = {
  readonly state: string;
  readonly kind: string;
  readonly canWrite: boolean;
  readonly hasReceivingZone: boolean;
  readonly undecidedCount: number;
  readonly notFullyReceived: number;
};

export type NextStepModel = {
  /** What the operator should do now. */
  readonly textKey: TranslationKey;
  /** What the same step looks like on the handheld (null once nothing is left to do). */
  readonly tsdKey: TranslationKey | null;
  readonly primary: StepAction | null;
  readonly secondary: StepAction | null;
  /** Why the primary button is disabled, or why a part of the flow is locked. */
  readonly blocked: { readonly key: TranslationKey; readonly params?: Record<string, string> } | null;
  readonly warn: { readonly key: TranslationKey; readonly params: Record<string, string> } | null;
  /** Lines can be received by hand from here. */
  readonly canReceiveLines: boolean;
  /** The primary button can be pressed. */
  readonly primaryEnabled: boolean;
};

const NEXT_TEXT: Record<string, TranslationKey> = {
  EXPECTED: 'rcv.next.EXPECTED',
  ARRIVED: 'rcv.next.ARRIVED',
  RECEIVING: 'rcv.next.RECEIVING',
  RECEIVED: 'rcv.next.RECEIVED',
  CLOSED: 'rcv.next.CLOSED',
  CANCELLED: 'rcv.next.CANCELLED',
};
const TSD_TEXT: Record<string, TranslationKey> = {
  EXPECTED: 'rcv.tsd.EXPECTED',
  ARRIVED: 'rcv.tsd.ARRIVED',
  RECEIVING: 'rcv.tsd.RECEIVING',
  RECEIVED: 'rcv.tsd.RECEIVED',
};

/**
 * The one place that decides what the Ops inbound page offers, from the state machine
 * (EXPECTED -> ARRIVED -> RECEIVING -> RECEIVED -> CLOSED) and what the caller may do.
 * Pure, so every state is unit-tested without rendering.
 */
export function nextStep(i: NextStepInput): NextStepModel {
  const open = i.state in TSD_TEXT;
  const base = {
    tsdKey: TSD_TEXT[i.state] ?? null,
    warn: null,
    canReceiveLines: false,
    primary: null,
    secondary: null,
    primaryEnabled: false,
    blocked: null,
  } satisfies Omit<NextStepModel, 'textKey'>;
  if (i.kind === 'RETURN') return { ...base, textKey: 'rcv.next.RETURN', tsdKey: null };
  const textKey = NEXT_TEXT[i.state] ?? 'rcv.next.CLOSED';
  if (!open) return { ...base, textKey, tsdKey: null };
  if (!i.canWrite) return { ...base, textKey, blocked: { key: 'rcv.blocked.noPermission' } };

  switch (i.state) {
    case 'EXPECTED':
      return {
        ...base, textKey, primary: 'START', secondary: 'ARRIVE',
        primaryEnabled: i.hasReceivingZone,
        blocked: i.hasReceivingZone ? { key: 'rcv.blocked.linesLocked' } : { key: 'rcv.blocked.noZone' },
      };
    case 'ARRIVED':
      return {
        ...base, textKey, primary: 'START',
        primaryEnabled: i.hasReceivingZone,
        blocked: i.hasReceivingZone ? { key: 'rcv.blocked.linesLocked' } : { key: 'rcv.blocked.noZone' },
      };
    case 'RECEIVING':
      return {
        ...base, textKey, primary: 'FINISH', primaryEnabled: true, canReceiveLines: i.hasReceivingZone,
        blocked: i.hasReceivingZone ? null : { key: 'rcv.blocked.noZone' },
        warn: i.notFullyReceived > 0 ? { key: 'rcv.warn.notAllReceived', params: { count: String(i.notFullyReceived) } } : null,
      };
    case 'RECEIVED':
      return {
        ...base, textKey, primary: 'CLOSE', primaryEnabled: i.undecidedCount === 0,
        blocked: i.undecidedCount > 0 ? { key: 'rcv.blocked.openDiscrepancy', params: { count: String(i.undecidedCount) } } : null,
      };
    default:
      return { ...base, textKey };
  }
}

export const undecidedCount = (lines: readonly InboundLine[]): number =>
  lines.filter((l) => l.discrepancy !== 'NONE' && l.responsible_party === 'UNDECIDED').length;

export const notFullyReceived = (lines: readonly InboundLine[]): number =>
  lines.filter((l) => Number(l.qty_received) < Number(l.qty_expected)).length;

/** A sentence for any receiving failure; everything else falls back to the shared mapping. */
export function describeReceivingError(t: TranslateFn, err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'RECEIVING.SHIPMENT_NOT_IN_RECEIVING': return t('rcv.err.notReceiving');
      case 'RECEIVING.INVALID_TRANSITION': return t('rcv.err.transition');
      case 'RECEIVING.OPEN_DISCREPANCY': return t('rcv.err.openDiscrepancy');
      case 'RECEIVING.SHORTAGE_NEEDS_CONFIRMATION': return t('rcv.err.shortage', { count: String(err.params.count ?? ''), short: String(err.params.short ?? '') });
      case 'RECEIVING.OVERAGE_UNRESOLVED': return t('rcv.err.overageUnresolved');
      case 'RECEIVING.DISPOSAL_EXCEEDS_HELD': return t('rcv.err.disposalExceeds');
      case 'RECEIVING.DISPOSAL_LOCATION_REQUIRED': return t('rcv.err.disposalLocation');
      case 'RECEIVING.ADJUST_BELOW_HELD': return t('rcv.err.adjustBelowHeld');
      case 'RECEIVING.NO_OVERAGE': return t('rcv.err.noOverage');
      case 'RECEIVING.OVERAGE_HELD': return t('rcv.err.overageHeld', { held: String(err.params.held ?? '') });
      case 'RECEIVING.OWNER_MISMATCH': return t('rcv.err.ownerMismatch');
      case 'SCAN.WRONG_ITEM': return t('rcv.err.wrongItem');
      case 'STOCK.TRACKED_UNSUPPORTED': return t('rcv.err.serial');
      case 'COMMON.VALIDATION_FAILED': {
        const field = (err.params as Record<string, unknown>).field;
        if (field === 'kind') return t('rcv.err.returnKind');
        if (field === 'skuId') return t('rcv.err.sku');
        if (field === 'locationId') return t('rcv.err.location');
        return t('err.validation');
      }
      case 'LOCATION.ARCHIVED':
        return t('rcv.err.location');
      case 'PUTAWAY.INVALID_DESTINATION':
        return t('put.err.destination');
      default:
    }
  }
  return describeActionError(t, err);
}
